import { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useFormContext } from 'react-hook-form'

import { formatNumber } from '@js/common/utils'

import { Form } from '@f/elements/HookForm'

import { QuizContext } from '../../context/QuizContext'

interface SliderElementProps {
  name: string
  label: string
  minValue: number
  maxValue: number
  valueStep: number
  valueSuffix: string
  valuePrefix: string
  stepId: string
}

export const SliderElement = ({
  name,
  label,
  maxValue,
  minValue,
  valueStep,
  stepId,
  valueSuffix
}: SliderElementProps) => {
  const { watch, getValues } = useFormContext()
  const context = useContext(QuizContext)

  const sliderValue = watch(name)

  const handleChangeComplete = () => {
    const values = getValues()

    const updatedValues = {
      ...values
    }

    context.actions.updateValues({
      stepID: stepId,
      valuesToSave: Object.entries(updatedValues).map(([key, value]) => ({
        name: key,
        value
      }))
    })
  }

  return (
    <div
      data-id={name}
      className='[ f-pos-rel f-m-t-020 f-m-h-auto f-c-ba-whi f-radius-card-large f-shadow-strong f-d-flc QuizApp-ExpectedValue QuizApp-long-card'>
      <SliderTooltip value={sliderValue} name={name} />
      <Form.Slider
        onChangeComplete={handleChangeComplete}
        defaultValue={sliderValue}
        maxValue={maxValue}
        minValue={minValue}
        type='Slider'
        step={valueStep}
        name={name}
        unit='m²'
      />

      <SliderLabels
        valueSuffix={valueSuffix}
        maxValue={maxValue}
        minValue={minValue}
      />
      {label && (
        <div className='[ f-c-ba-bac015 f-t-body f-p-a-020 f-c-fo-neu200 f-t-centre f-radius-card-large-bottom f-t-bold ] QuizApp-long-card-label'>
          {label}
        </div>
      )}
    </div>
  )
}

interface SliderTooltipProps {
  value: number
  name?: string
}

const SliderTooltip = ({ value, name }: SliderTooltipProps) => {
  const [mountingPoint, setMountingPoint] = useState(null)

  useEffect(() => {
    const selector = `div[data-id="${name}"] .rc-slider-handle`
    const element = document.querySelector(selector)
    setMountingPoint(element)
  }, [name, value])

  if (!mountingPoint) {
    return null
  }

  return createPortal(
    <div className='f-t-mini f-c-fo-whi FinanceSlider-tooltip'>
      {`${formatNumber(value)} m²`}
    </div>,
    mountingPoint
  )
}

interface SliderLabelsProps {
  minValue: number
  maxValue: number
  valueSuffix?: string
  valuePrefix?: string
}

const SliderLabels = ({
  minValue,
  maxValue,
  valueSuffix,
  valuePrefix
}: SliderLabelsProps) => {
  if (valueSuffix === '%') {
    return <PercentageSliderLabels />
  }
  return (
    <div className='[ f-p-t-010 f-p-b-040 f-d-flr ]'>
      <span className='[ f-t-micro f-c-fo-mut f-t-left f-fl-expand ]'>
        {minValue > 0
          ? `${valuePrefix ? `${valuePrefix} ` : ''}${minValue.toLocaleString(
              'DE-de'
            )}${valueSuffix ? ` ${valueSuffix}` : ''}`
          : 'Keine Angabe'}
      </span>
      <span className='[ f-t-micro f-c-fo-mut f-t-centre f-fl-expand ]'>
        {valuePrefix ? `${valuePrefix} ` : ''}
        {((maxValue + minValue) / 2).toLocaleString('DE-de')}
        {valueSuffix ? ` ${valueSuffix}` : ''}
      </span>
      <span className='[ f-t-micro f-c-fo-mut f-t-right f-fl-expand ]'>
        &gt; {valuePrefix ? `${valuePrefix} ` : ''}
        {maxValue.toLocaleString('DE-de')}
        {valueSuffix ? ` ${valueSuffix}` : ''}
      </span>
    </div>
  )
}

const PercentageSliderLabels = () => {
  return (
    <div className='[ f-p-t-010 f-p-b-040 f-d-flr ]'>
      <span className='[ f-t-micro f-c-fo-mut f-t-left f-fl-expand ]'>
        10 %
      </span>
      <span className='[ f-t-micro f-c-fo-mut f-t-centre f-fl-expand ]'>
        50 %
      </span>
      <span className='[ f-t-micro f-c-fo-mut f-t-right f-fl-expand ]'>
        90 %
      </span>
    </div>
  )
}
